import {
  Attributes,
  Experiment,
  GrowthBook,
  Result,
  FeatureApiResponse,
} from '../../contexts';
import { tagContextMatches } from '../../utils/tagContextMatches';

import { ILogger } from '@monofront/types';

export class GrowthbookService {
  public growthbook: GrowthBook;
  public untrackedGrowthbook: GrowthBook;

  constructor(
    enableDevMode: boolean,
    private logger: ILogger,
    trackingCallback?: (
      experiment: Experiment<any>,
      result: Result<any>,
    ) => void,
  ) {
    this.growthbook = new GrowthBook({
      enableDevMode: enableDevMode,
      trackingCallback: (experiment, result) => {
        this.logger.log(`Viewed Experiment: ${experiment} ${result}`);

        trackingCallback?.(experiment, result);
      },
    });

    this.untrackedGrowthbook = new GrowthBook({
      trackingCallback: undefined,
    });
  }

  setPayload(...params: Parameters<typeof this.growthbook.setPayload>) {
    this.growthbook.setPayload(...params);
    this.untrackedGrowthbook.setPayload(...params);
  }

  getFeatureFlags = async (
    endpoint: string,
  ): Promise<FeatureApiResponse | undefined> => {
    const response = await Promise.race([
      fetch(endpoint),
      new Promise<Response>((resolve) =>
        setTimeout(() => resolve({ ok: false } as Response), 30000),
      ),
    ]);

    if (!response.ok) {
      this.logger.warn('Could not get feature flags', {
        endpoint,
        httpStatus: response.status,
        response: response.text(),
      });

      return undefined;
    }

    const data: FeatureApiResponse = await response.json();

    return data;
  };

  getFeatureValueWithoutTracking = <T>(feature: string, defaultValue: T): T => {
    return this.untrackedGrowthbook.evalFeature(feature).value ?? defaultValue;
  };

  enterExperimentFromFeatureFlag = (feature: string): void => {
    const value = this.growthbook.evalFeature(feature);
    const untrackedValue = this.getFeatureValueWithoutTracking(feature, '');

    if (value.value !== untrackedValue) {
      this.logger.error(
        'Experiment entered from feature flag with a different untracked value',
        {
          feature,
          value,
          untrackedValue,
        },
      );
    }
  };

  setGrowthbookAttributes = (attributes: Attributes): void => {
    this.growthbook.setAttributes({
      ...this.growthbook.getAttributes(),
      ...attributes,
      currentTime: Math.floor(Date.now() / 1000), // this attribute enables the scheduled configuration
    });
    this.untrackedGrowthbook.setAttributes({
      ...this.untrackedGrowthbook.getAttributes(),
      ...attributes,
      currentTime: Math.floor(Date.now() / 1000), // this attribute enables the scheduled configuration
    });
  };

  public tagContextMatches = tagContextMatches;
}
